/**
 * Capa de datos pública: cuando Supabase está configurado va contra la BD,
 * si no, usa los datos demo locales. Las páginas públicas (Home, Catálogo,
 * Sedes, Vacantes) usan SIEMPRE estas funciones — nunca llaman a
 * supabase.from() directamente — para que el sitio funcione sin BD.
 *
 * Las páginas privadas (Login, Dashboard, Admin) sí usan supabase directo
 * porque sin sesión no tienen sentido.
 */
import { supabase, IS_SUPABASE_CONFIGURED } from './supabase';
import type { Product, Vacancy } from './database.types';
import { DEMO_PRODUCTS, DEMO_SEDES, DEMO_VACANCIES } from './demoData';

export interface PublicSede {
  id: string;
  nombre: string;
  direccion: string | null;
  telefono: string | null;
  whatsapp: string | null;
  horario: string | null;
  icon: string | null;
  maps_embed_url: string | null;
  maps_link_url: string | null;
  badge: string | null;
  is_active: boolean;
  sort_order: number;
}

export async function getProducts(opts?: { ids?: string[]; limit?: number }): Promise<Product[]> {
  if (!IS_SUPABASE_CONFIGURED) {
    let rows = DEMO_PRODUCTS.slice();
    if (opts?.ids) rows = rows.filter((p) => opts.ids!.includes(p.id));
    rows.sort((a, b) =>
      a.category.localeCompare(b.category) || a.sort_order - b.sort_order,
    );
    return opts?.limit ? rows.slice(0, opts.limit) : rows;
  }

  let q = supabase.from('products').select('*').eq('is_active', true);
  if (opts?.ids) q = q.in('id', opts.ids);
  q = q.order('category').order('sort_order');
  if (opts?.limit) q = q.limit(opts.limit);
  const { data, error } = await q;
  if (error) {
    console.warn('[data] products fetch fallback:', error.message);
    return DEMO_PRODUCTS;
  }
  return data ?? [];
}

export async function getFeaturedProducts(): Promise<Product[]> {
  // Los 3 destacados del demo (mismo orden)
  const ids = ['espaldilla', 'el-dorado', 'chorizo-especial'];
  const rows = await getProducts({ ids });
  return rows.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
}

export async function getSedes(): Promise<PublicSede[]> {
  if (!IS_SUPABASE_CONFIGURED) return DEMO_SEDES;
  const { data, error } = await supabase
    .from('sedes')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');
  if (error) {
    console.warn('[data] sedes fetch fallback:', error.message);
    return DEMO_SEDES;
  }
  return (data ?? []) as PublicSede[];
}

export async function getVacancies(limit?: number): Promise<Vacancy[]> {
  if (!IS_SUPABASE_CONFIGURED) {
    return limit ? DEMO_VACANCIES.slice(0, limit) : DEMO_VACANCIES;
  }
  let q = supabase
    .from('vacancies')
    .select('*')
    .eq('activa', true)
    .order('created_at', { ascending: false });
  if (limit) q = q.limit(limit);
  const { data, error } = await q;
  if (error) {
    console.warn('[data] vacancies fetch fallback:', error.message);
    return limit ? DEMO_VACANCIES.slice(0, limit) : DEMO_VACANCIES;
  }
  return (data ?? []) as Vacancy[];
}
